import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import type { SkillAgent, SkillTeam, SkillType, SkillVisibility } from '../../../../shared/types';

const ALL_TEAMS: SkillTeam[] = [
  'Business BR', 'Business SPLA', 'Business',
  'Compensations and Experience', 'Creative Studio', 'Data', 'Development',
  'Enterprise', 'Finance', 'Growth Partners', 'Growth', 'IT', 'Marketing',
  'New Solutions', 'Operations', 'Owned Media Lab', 'Paid Media Lab', 'People', 'Performance',
  'Retail Media', 'Sales', 'Sharing', 'Strategy & Ops', 'Strategy & Planning',
  'Startup', 'Talent Acquisition', 'Tech & Product', 'Tech', 'US', 'We Care',
];

interface Props {
  skill: SkillAgent;
  open: boolean;
  onClose: () => void;
}

export function EditSkillModal({ skill, open, onClose }: Props) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const [name, setName] = useState(skill.name);
  const [description, setDescription] = useState(skill.description);
  const [type, setType] = useState<SkillType>(skill.type);
  const [team, setTeam] = useState<SkillTeam>(skill.team);
  const [tagsRaw, setTagsRaw] = useState(skill.tags.join(', '));
  const [visibility, setVisibility] = useState<SkillVisibility>(skill.visibility);
  const [useCaseText, setUseCaseText] = useState(skill.useCaseText ?? '');

  // Reset form when skill changes
  useEffect(() => {
    setName(skill.name);
    setDescription(skill.description);
    setType(skill.type);
    setTeam(skill.team);
    setTagsRaw(skill.tags.join(', '));
    setVisibility(skill.visibility);
    setUseCaseText(skill.useCaseText ?? '');
  }, [skill.id]);

  const updateMutation = trpc.skills.updateMeta.useMutation({
    onSuccess: () => {
      utils.skills.getAll.invalidate();
      toast.success(t('skills.editskillmodal.skill_actualizada'));
      onClose();
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const handleSave = () => {
    if (!name.trim()) return;
    const tags = tagsRaw.split(',').map((tag) => tag.trim()).filter(Boolean);
    updateMutation.mutate({
      skillId: skill.id,
      name: name.trim(),
      description: description.trim(),
      type,
      team,
      tags,
      visibility,
      useCaseText: useCaseText.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('skills.editskillmodal.editar_skill')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">

          <div className="space-y-1.5">
            <Label>Nombre</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('skills.editskillmodal.descripcion')}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={type} onValueChange={(v) => setType(v as SkillType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="skill">Skill</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="plugin">Plugin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Visibilidad</Label>
              <Select value={visibility} onValueChange={(v) => setVisibility(v as SkillVisibility)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">{t('skills.editskillmodal.publica')}</SelectItem>
                  <SelectItem value="team">{t('skills.editskillmodal.solo_mi_equipo')}</SelectItem>
                  <SelectItem value="restricted">Restringida</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Equipo</Label>
            <Select value={team} onValueChange={(v) => setTeam(v as SkillTeam)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-56">
                {ALL_TEAMS.map((teamName) => (
                  <SelectItem key={teamName} value={teamName}>{teamName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Tags <span className="text-xs text-muted-foreground">{t('skills.editskillmodal.separados_por_coma')}</span></Label>
            <Input
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder={t('skills.editskillmodal.growth_email_outreach')}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('skills.editskillmodal.caso_de_uso')} <span className="text-xs text-muted-foreground">(opcional)</span></Label>
            <Textarea
              value={useCaseText}
              onChange={(e) => setUseCaseText(e.target.value)}
              rows={2}
              className="resize-none"
              placeholder={t('skills.editskillmodal.para_que_sirve_y_cuando_usar')}
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>
              {t('common.ui.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!name.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Guardando…' : t('skills.editskillmodal.guardar_cambios')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
